import { useId } from 'react';
import { ArrowLeft, ArrowRight, ExternalLink, RotateCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import type { CanvasHost } from '../hooks/use-storefront-canvas-host';
import { useWebsiteT } from '../hooks/use-website-t';
import type { FrameDevice } from '../lib/frame-viewport';
import { displayAddress } from '../lib/live-store-address';
import { DeviceToggle } from './device-toggle';

export interface FrameBrowserBarProps {
  readonly host: CanvasHost;
  readonly storeSlug: string;
  readonly device: FrameDevice;
  readonly onDeviceChange: (device: FrameDevice) => void;
  /** The published store, or null before anything is published. */
  readonly liveUrl: string | null;
  readonly onOpenLive: () => void;
}

/**
 * The viewing controls of Dukanti around the storefront: back, forward,
 * refresh, the read-only address, desktop and mobile, and the live store.
 * They belong to the editor, never to the storefront inside the frame.
 */
export function FrameBrowserBar({
  host,
  storeSlug,
  device,
  onDeviceChange,
  liveUrl,
  onOpenLive,
}: FrameBrowserBarProps): React.JSX.Element {
  const { t } = useWebsiteT();
  const { route } = host;
  const unavailableId = useId();

  return (
    <div
      data-testid="frame-browser-bar"
      className="border-border bg-card flex flex-wrap items-center gap-2 border-b px-3 py-2"
    >
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="size-8"
        aria-label={t('admin.websiteTemplates.frame.back')}
        disabled={!route.canGoBack}
        onClick={() => host.navigate({ kind: 'history', direction: 'back' })}
      >
        <ArrowLeft aria-hidden="true" className="size-4 rtl:rotate-180" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="size-8"
        aria-label={t('admin.websiteTemplates.frame.forward')}
        disabled={!route.canGoForward}
        onClick={() => host.navigate({ kind: 'history', direction: 'forward' })}
      >
        <ArrowRight aria-hidden="true" className="size-4 rtl:rotate-180" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="size-8"
        aria-label={t('admin.websiteTemplates.editor.reload')}
        title={t('admin.websiteTemplates.editor.reload')}
        onClick={host.refresh}
      >
        <RotateCw aria-hidden="true" className="size-4" />
      </Button>

      <Input
        readOnly
        aria-label={t('admin.websiteTemplates.frame.address')}
        data-testid="frame-address"
        dir="ltr"
        value={displayAddress(storeSlug, route.displayPath)}
        className="bg-admin-surface-muted text-muted-foreground h-8 min-w-0 flex-1 text-start"
      />

      <DeviceToggle device={device} onChange={onDeviceChange} />

      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="size-8"
        aria-label={t('admin.websiteTemplates.editor.openLive')}
        title={
          liveUrl === null
            ? t('admin.websiteTemplates.frame.openLiveUnavailable')
            : t('admin.websiteTemplates.editor.openLive')
        }
        disabled={liveUrl === null}
        aria-describedby={liveUrl === null ? unavailableId : undefined}
        onClick={onOpenLive}
      >
        <ExternalLink aria-hidden="true" className="size-4 rtl:-scale-x-100" />
      </Button>
      {liveUrl === null ? (
        // Why the icon button is off, for screen readers (the title is only a tooltip).
        <span id={unavailableId} className="sr-only">
          {t('admin.websiteTemplates.frame.openLiveUnavailable')}
        </span>
      ) : null}
    </div>
  );
}
